"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2, X } from "lucide-react";
import { type Accept, useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { Button } from "@repo/ui";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui";
import { useIntersectionObserver } from "@/hooks/ui-helpers/use-intersection-observer";
import { useDebounce } from "@/lib/client-utils";
import { cn } from "@/lib/utils";
import { usePyloMediaRelations } from "@/hooks/use-pylo-media-relations";
import { useMediaKindFacets, useMediaList, useMediaSummary } from "@/hooks/use-media-library";
import { useMediaUpload } from "@/hooks/use-media-upload";
import { deletePyloMedia, getPyloMediaById, type PyloMedia } from "@/services/pylo-media.service";
import {
  EMPTY_MEDIA_FILTER,
  type MediaKind,
  type MediaLibraryFilter,
  type MediaSort,
  type UsedByFilter,
} from "@/lib/media-library-filter";
import { MediaFacetRail } from "./media-facet-rail";
import { MediaResults } from "./media-results";
import { MediaInspector } from "./media-inspector";
import { MediaToolbar, type MediaViewMode } from "./media-toolbar";

export interface MediaBrowserProps {
  /**
   * `pick` turns a click into the selection gesture and drops the destructive
   * bulk actions; `browse` keeps click-to-inspect plus bulk delete.
   */
  mode?: "browse" | "pick";
  /** Folds the facet rail into the toolbar and moves the inspector into an overlay. */
  compact?: boolean;
  multiple?: boolean;
  /** Relation constraint — files outside it are never shown, see `MediaLibraryFilter`. */
  allowedMimeTypes?: Array<string>;
  /** Restricts what the built-in upload accepts; mirrors `allowedMimeTypes`. */
  accept?: Accept;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>, files: Array<PyloMedia>) => void;
}

export const MediaBrowser = ({
  mode = "browse",
  compact = false,
  multiple = true,
  allowedMimeTypes,
  accept,
  selectedIds: controlledSelection,
  onSelectionChange,
}: MediaBrowserProps) => {
  const queryClient = useQueryClient();
  const relations = usePyloMediaRelations();
  const isPicking = mode === "pick";

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [filterState, setFilterState] = useState<MediaLibraryFilter>(EMPTY_MEDIA_FILTER);
  const [view, setView] = useState<MediaViewMode>("grid");
  const [activeMedia, setActiveMedia] = useState<PyloMedia | null>(null);
  const [ownSelection, setOwnSelection] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<Array<string> | null>(null);
  // Freshly uploaded files, kept until a refetch of the filtered list carries them.
  const [uploaded, setUploaded] = useState<Array<PyloMedia>>([]);

  const selectedIds = controlledSelection ?? ownSelection;

  const filter = useMemo<MediaLibraryFilter>(
    () => ({ ...filterState, search: debouncedSearch, lockedMimeTypes: allowedMimeTypes }),
    [filterState, debouncedSearch, allowedMimeTypes],
  );

  const list = useMediaList(filter, relations);
  const { counts } = useMediaKindFacets(filter, relations);
  const summary = useMediaSummary(filter, relations);

  const listed = useMemo(() => list.data?.pages.flatMap((page) => page.data) ?? [], [list.data]);
  const files = useMemo(() => {
    const listedIds = new Set(listed.map((media) => media.id));
    return [...uploaded.filter((media) => !listedIds.has(media.id)), ...listed];
  }, [uploaded, listed]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMore = useCallback(() => {
    if (list.hasNextPage && !list.isFetchingNextPage) list.fetchNextPage();
  }, [list]);
  useIntersectionObserver(sentinelRef as unknown as React.RefObject<Element>, loadMore, {
    once: false,
  });

  // Escape closes the details panel first. Captured on document so it lands before
  // the surrounding dialog's own dismissal — otherwise inspecting a file inside the
  // picker and hitting Escape would throw away the whole selection.
  useEffect(() => {
    if (!activeMedia) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setActiveMedia(null);
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [activeMedia]);

  const toggleActive = (media: PyloMedia) =>
    setActiveMedia((previous) => (previous?.id === media.id ? null : media));

  const selectKind = (kind?: MediaKind) => setFilterState((previous) => ({ ...previous, kind }));

  const setUsedBy = (usedBy?: UsedByFilter) =>
    setFilterState((previous) => ({ ...previous, usedBy }));

  const setSort = (sort: MediaSort) => setFilterState((previous) => ({ ...previous, sort }));

  const reset = () => {
    setFilterState(EMPTY_MEDIA_FILTER);
    setSearch("");
  };

  /**
   * `extra` covers files that exist but are not in `files` yet — an upload
   * selects itself before the state holding it has been committed.
   */
  const applySelection = (next: Set<string>, extra: Array<PyloMedia> = []) => {
    if (!controlledSelection) setOwnSelection(next);
    const pool = [...extra, ...files];
    onSelectionChange?.(
      next,
      Array.from(next)
        .map((id) => pool.find((media) => media.id === id))
        .filter(Boolean) as Array<PyloMedia>,
    );
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // A single-value relation can hold one file, so picking replaces rather than adds.
    applySelection(isPicking && !multiple ? new Set(next.has(id) ? [id] : []) : next);
  };

  const refreshLibrary = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["media-library"] });
    queryClient.invalidateQueries({ queryKey: ["media-library-kind-facets"] });
    queryClient.invalidateQueries({ queryKey: ["media-library-summary"] });
  }, [queryClient]);

  const onUploaded = async (mediaId: string) => {
    let media: PyloMedia;
    try {
      media = await getPyloMediaById(mediaId);
    } catch (error) {
      // Rethrown so the upload's own tile shows the failure, not just a toast.
      toast.error("Uploaded, but the file could not be read back.");
      throw error;
    }

    setUploaded((previous) => [media, ...previous]);
    refreshLibrary();

    if (!isPicking) return;
    const next = multiple ? new Set(selectedIds).add(media.id) : new Set([media.id]);
    applySelection(next, [media]);
  };

  const { uploads, start } = useMediaUpload({ onCompleted: onUploaded });

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: (accepted) => accepted.length > 0 && start(accepted),
    onDropRejected: (rejections) =>
      toast.error(`${rejections.length} file(s) are not an accepted file type.`),
    accept,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  // A finished upload keeps its tile only until the library row replaces it.
  const inFlight = uploads.filter(
    (upload) => !upload.mediaId || !uploaded.some((media) => media.id === upload.mediaId),
  );

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const ids = pendingDelete;
    setPendingDelete(null);

    try {
      await deletePyloMedia(ids);
      toast.success(ids.length === 1 ? "File deleted" : `${ids.length} files deleted`);
      if (activeMedia && ids.includes(activeMedia.id)) setActiveMedia(null);
      setUploaded((previous) => previous.filter((media) => !ids.includes(media.id)));
      applySelection(new Set());
      refreshLibrary();
    } catch {
      toast.error("Could not delete. The file may still be connected to a record.");
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        {!compact && <MediaFacetRail filter={filter} counts={counts} onSelectKind={selectKind} />}

        <div className="flex min-w-0 flex-1 flex-col">
          <MediaToolbar
            filter={filter}
            relations={relations}
            search={search}
            view={view}
            summary={summary}
            compact={compact}
            counts={counts}
            onSearchChange={setSearch}
            onSelectKind={selectKind}
            onSetUsedBy={setUsedBy}
            onSetSort={setSort}
            onSetView={setView}
            onReset={reset}
            onUpload={open}
          />

          {!isPicking && selectedIds.size > 0 && (
            <div className="flex items-center gap-3 border-b bg-muted/40 px-4 py-2">
              <span className="text-[13px] font-medium">{selectedIds.size} selected</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-[13px] text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setPendingDelete(Array.from(selectedIds))}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 gap-1.5 text-[13px]"
                onClick={() => applySelection(new Set())}
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            </div>
          )}

          <div
            {...getRootProps()}
            className={cn("relative min-h-0 flex-1 overflow-y-auto bg-gray-50/60 p-4")}
          >
            <input {...getInputProps()} />

            <MediaResults
              files={files}
              uploads={inFlight}
              view={view}
              activeId={activeMedia?.id}
              selectedIds={selectedIds}
              isLoading={list.isLoading}
              mode={mode}
              showInspectAction={compact}
              onActivate={toggleActive}
              onToggleSelect={toggleSelect}
            />
            <div ref={sentinelRef} className="h-8" />
            {list.isFetchingNextPage && (
              <p className="py-2 text-center text-[12px] text-muted-foreground">Loading more…</p>
            )}

            {isDragActive && (
              <div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/10 text-[13px] font-medium text-primary">
                Drop to upload to the library
              </div>
            )}
          </div>
        </div>

        {activeMedia && (
          <MediaInspector
            media={activeMedia}
            relations={relations}
            overlay={compact}
            onClose={() => setActiveMedia(null)}
            onDelete={isPicking ? undefined : (media) => setPendingDelete([media.id])}
          />
        )}
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {pendingDelete?.length === 1 ? "this file" : `${pendingDelete?.length} files`}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Records still referencing the file will lose it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
